import React from 'react';
import { ThumbsUp } from 'lucide-react';
import type { RoadmapItem as RoadmapItemType } from '../hooks/useRoadmap';

interface RoadmapItemProps {
  item: RoadmapItemType;
  onVote: (itemId: string) => Promise<boolean>;
}

const RoadmapItem: React.FC<RoadmapItemProps> = ({ item, onVote }) => {
  const [isVoting, setIsVoting] = React.useState(false);
  
  const handleVote = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (isVoting) return;
    
    setIsVoting(true);
    try {
      await onVote(item.id);
    } finally {
      setIsVoting(false);
    }
  };
  
  // Determine border color based on status
  let borderColorClass = 'border-gray-200';
  if (item.status === 'in_progress') {
    borderColorClass = 'border-haus-light-green';
  } else if (item.status === 'completed') {
    borderColorClass = 'border-haus-light-olive';
  }
  
  // Determine category badge style
  let categoryBgClass = 'bg-haus-light-brown/10 text-haus-dark-brown';
  if (item.status === 'in_progress') {
    categoryBgClass = 'bg-haus-light-green/10 text-haus-dark-green';
  } else if (item.status === 'completed') {
    categoryBgClass = 'bg-haus-light-olive/10 text-haus-dark-olive';
  }
  
  // Format the category for display
  const formatCategory = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ');
  };

  return (
    <div className={`p-4 border ${borderColorClass} rounded-lg shadow-sm transition-all hover:shadow-md`}>
      <h3 className="font-medium">{item.title}</h3>
      {/* <p className="text-sm text-gray-600 mt-1">{item.expected_timeline}</p> */}
      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
      <div className="mt-3 flex justify-between items-center">
        <div className="text-sm">
          <span className={`${categoryBgClass} px-2 py-1 rounded-full`}>
            {formatCategory(item.category)}
          </span>
        </div>
        <button 
          onClick={handleVote}
          disabled={isVoting}
          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-haus-light-white hover:bg-haus-lighter-olive
            ${item.user_has_voted 
              ? 'bg-haus-dark-green' 
              : 'bg-haus-light-olive'
            } transition-colors`}
          aria-label={item.user_has_voted ? "Remove vote" : "Vote for this feature"}
        >
          <span>{item.votes_count}</span>
          <ThumbsUp size={16} className={isVoting ? 'animate-pulse' : ''} />
        </button>
      </div>
    </div>
  );
};

export default RoadmapItem; 